import type { LogDescriptor } from '../core/log/client';
import { leafForSct } from '../core/sct/embedded';
import { leafIndexFromExtensions } from '../core/rfc6962/sct';
import type { Sct, SignedEntry } from '../core/rfc6962/types';
import { getClient } from './state/clients';
import { registry } from './state/logs';
import { refreshTreeHead } from './state/treeHeads';
import { logPath } from './nav';

/**
 * For each SCT, a deep link to the inclusion-proof page of its log: tiled logs are addressed by the
 * leaf_index extension, RFC 6962 logs by the leaf hash the log must have stored.
 */
export async function inclusionLinksFor(scts: Sct[], entry: SignedEntry | undefined): Promise<Array<string | undefined>> {
  const reg = registry.value;
  return Promise.all(
    scts.map(async (sct) => {
      const log: LogDescriptor | undefined = reg.byLogId(sct.logId);
      if (!log) return undefined;
      const idx = leafIndexFromExtensions(sct.extensions);
      if (log.kind === 'tiled') return idx !== undefined ? logPath(log, 'inclusion', { index: idx }) : undefined;
      if (!entry) return undefined;
      const { hash } = await leafForSct(sct, entry);
      return logPath(log, 'inclusion', { hash: hash.toHex() });
    }),
  );
}

export type LocatedEntry = { status: 'found'; log: LogDescriptor; index: number } | { status: 'unknown-log' } | { status: 'no-entry' } | { status: 'error'; error: unknown };

const located = new Map<string, Promise<LocatedEntry>>();

/**
 * Finds the log entry an SCT was issued for (the precertificate entry, for embedded SCTs):
 * tiled logs carry the index in the SCT itself; RFC 6962 logs are asked for the proof by leaf hash.
 */
export function locateSctEntry(sct: Sct, entry: SignedEntry | undefined): Promise<LocatedEntry> {
  const log = registry.value.byLogId(sct.logId);
  if (!log) return Promise.resolve({ status: 'unknown-log' });
  const idx = leafIndexFromExtensions(sct.extensions);
  if (idx !== undefined) return Promise.resolve({ status: 'found', log, index: idx });
  if (!entry) return Promise.resolve({ status: 'no-entry' });
  const key = `${log.id}|${sct.timestamp}|${sct.signature.signature.toHex().slice(0, 32)}`;
  let p = located.get(key);
  if (!p) {
    p = (async (): Promise<LocatedEntry> => {
      try {
        const client = getClient(log);
        if (!client.supportsHashLookup) return { status: 'no-entry' };
        const { hash } = await leafForSct(sct, entry);
        const head = await refreshTreeHead(client);
        const proof = await client.getInclusionProof({ leafHash: hash }, head.treeSize);
        return { status: 'found', log, index: proof.leafIndex };
      } catch (error) {
        return { status: 'error', error };
      }
    })();
    located.set(key, p);
  }
  return p;
}
